import { zodResolver } from '@hookform/resolvers/zod'
import { router, useNavigation } from 'expo-router'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldMultiSelect from '@/components/form/FormFieldMultiSelect'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldSpecies from '@/components/fosters/cards/formFields/FormFieldSpecies'
import Card from '@/components/shared/Card'
import Page from '@/components/shared/Page'
import UniButton from '@/components/unistyles/UniButton'
import UniDivider from '@/components/unistyles/UniDivider'
import UniText from '@/components/unistyles/UniText'
import Species from '@/enums/species'
import useAutoSave from '@/hooks/useAutoSave'
import OrganizationService from '@/services/organizationService'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

const vaccineFormSchema = z.object({
  autoSchedule: z.boolean().default(false),
  delayFromVaccineId: z.string().uuid().nullable().optional(),
  initialBoosterCount: z.coerce.number().int().nullable().optional(),
  initialBoosterCadenceMaxWeeks: z.coerce.number().int().nullable().optional(),
  initialBoosterCadenceMinWeeks: z.coerce.number().int().nullable().optional(),
  initialBoosterRecurringAgeWeeks: z.coerce
    .number()
    .int()
    .nullable()
    .optional(),
  longTermBoosterCadence: z.coerce.number().int().max(3).nullable().optional(),
  minAgeWeeks: z.coerce.number().int().positive(),
  name: z.string(),
  species: z.nativeEnum(Species),
  supersededByVaccineIds: z.array(z.string().uuid()).optional(),
})

type VaccineForm = z.infer<typeof vaccineFormSchema>

interface Props {
  vaccineId: string
}

const OrganizationEditVaccine = ({ vaccineId }: Props) => {
  const navigation = useNavigation()
  const utils = trpc.useContext()

  const isNewVaccine = vaccineId === 'new'

  const { currentOrganization } = OrganizationService.useCurrentOrganization()

  const vaccineQuery = trpc.vaccine.byId.useQuery(
    { id: vaccineId },
    {
      enabled: !isNewVaccine,
    }
  )

  const vaccineListQuery = trpc.vaccine.list.useQuery(
    {
      organizationId: currentOrganization?.id || '',
    },
    {
      enabled: !!currentOrganization,
    }
  )

  const vaccine =
    vaccineListQuery.data?.find((v) => v.id === vaccineId) || vaccineQuery.data

  const createVaccineMutation = trpc.vaccine.create.useMutation()
  const updateVaccineMutation = trpc.vaccine.update.useMutation()
  const deleteVaccineMutation = trpc.vaccine.delete.useMutation()

  const defaultValues = useMemo(() => {
    return {
      autoSchedule: vaccine?.autoSchedule || false,
      delayFromVaccineId: vaccine?.delayFromVaccineId || null,
      initialBoosterCount: vaccine?.initialBoosterCount || null,
      initialBoosterCadenceMaxWeeks:
        vaccine?.initialBoosterCadenceMaxWeeks || null,
      initialBoosterCadenceMinWeeks:
        vaccine?.initialBoosterCadenceMinWeeks || null,
      initialBoosterRecurringAgeWeeks:
        vaccine?.initialBoosterRecurringAgeWeeks || null,
      longTermBoosterCadence: vaccine?.longTermBoosterCadence || null,
      minAgeWeeks: vaccine?.minAgeWeeks || 0,
      name: vaccine?.name || '',
      species: (vaccine?.species || null) as Species,
      supersededByVaccineIds: vaccine?.supersededByVaccineIds || [],
    }
  }, [vaccine])

  const form = useForm<VaccineForm>({
    defaultValues,
    resolver: zodResolver(vaccineFormSchema),
  })

  const { control, formState, handleSubmit, reset } = form

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const onSave = (data: VaccineForm) => {
    if (isNewVaccine && createVaccineMutation.isPending) {
      return
    }

    const payload = {
      ...data,
      delayFromVaccineId: data.delayFromVaccineId || null,
      initialBoosterCount: data.initialBoosterCount || null,
      initialBoosterCadenceMaxWeeks: data.initialBoosterCadenceMaxWeeks || null,
      initialBoosterCadenceMinWeeks: data.initialBoosterCadenceMinWeeks || null,
      initialBoosterRecurringAgeWeeks:
        data.initialBoosterRecurringAgeWeeks || null,
      longTermBoosterCadence: data.longTermBoosterCadence || null,
      minAgeWeeks: data.minAgeWeeks,
      organizationId: currentOrganization?.id || '',
      species: (data.species || null) as 'Dog' | 'Cat',
      supersededByVaccineIds: data.supersededByVaccineIds || [],
    }

    if (isNewVaccine) {
      createVaccineMutation.mutate(payload, {
        onSuccess: (newVaccine) => {
          utils.vaccine.list.invalidate()
          router.replace(
            buildOrgPath(`/org-settings/${currentOrganization?.id}/vaccines/${newVaccine.id}`)
          )
        },
      })
    } else {
      updateVaccineMutation.mutate({ id: vaccineId, ...payload })
    }
  }

  useEffect(() => {
    if (!isNewVaccine && !vaccine?.name) {
      return
    }

    navigation.setOptions({
      title: isNewVaccine ? 'New Vaccine' : `${vaccine?.name} Settings` || '',
    })
  })

  const wrappedOnSave = () => {
    if (isNewVaccine && createVaccineMutation.isPending) {
      return
    }
    handleSubmit(onSave)()
  }

  useAutoSave({
    form,
    defaultValues,
    onSave: wrappedOnSave,
  })

  if (!vaccineListQuery.isSuccess) {
    return null
  }

  const vaccineList =
    vaccineListQuery?.data
      ?.sort((a, b) => a.name.localeCompare(b.name))
      ?.map((v) => ({
        label: v.name,
        value: v.id,
      })) || []

  const isFormDisabled =
    !currentOrganization?.subscriptionActive ||
    (isNewVaccine && createVaccineMutation.isPending)

  return (
    <Page viewId="organization-new-vaccine">
      <Card title="Vaccine Info">
        <View style={styles.vaccineSection}>
          <UniText variant="headlineSmall">Basic Information</UniText>
          <View style={styles.vaccineRow}>
            <FormFieldText
              control={control}
              disabled={isFormDisabled}
              errors={formState.errors}
              fieldName="name"
              label="Name"
              required={true}
              style={styles.flexContainer}
            />
            <FormFieldSpecies
              control={control}
              disabled={isFormDisabled}
              errors={formState.errors}
              style={styles.flexContainer}
            />
          </View>
          <UniText>
            Set the minimum age (in weeks) when an animal can receive this
            vaccine. This will be used to calculate the initial due date when
            adding the vaccine to a foster profile.
          </UniText>
          <FormFieldText
            control={control}
            disabled={isFormDisabled}
            errors={formState.errors}
            fieldName="minAgeWeeks"
            keyboardType="numeric"
            label="Minimum Age in weeks"
            required={true}
          />
          <UniText>
            Optional: Enable automatic scheduling to include this vaccine in new
            foster profiles when no previous vaccination history exists.
          </UniText>
          <FormFieldCheckbox
            control={control}
            disabled={isFormDisabled}
            fieldName="autoSchedule"
            label="Auto Schedule"
            required={false}
            status="checked"
          />
          <UniDivider />
        </View>
        <View style={styles.vaccineSection}>
          <UniText variant="headlineSmall">Booster Schedule</UniText>
          <UniText>
            Optional: If this vaccine requires follow-up boosters, specify the
            valid time range (in weeks) between shots.
          </UniText>
          <View style={styles.vaccineRow}>
            <FormFieldText
              control={control}
              disabled={isFormDisabled}
              errors={formState.errors}
              fieldName="initialBoosterCadenceMinWeeks"
              keyboardType="numeric"
              label="Min Weeks"
              required={true}
              style={styles.flexContainer}
            />
            <UniText>-</UniText>
            <FormFieldText
              control={control}
              disabled={isFormDisabled}
              errors={formState.errors}
              fieldName="initialBoosterCadenceMaxWeeks"
              keyboardType="numeric"
              label="Max Weeks"
              required={true}
              style={styles.flexContainer}
            />
          </View>
          <UniText>Optional: Number of follow-up booster shots needed.</UniText>
          <FormFieldText
            control={control}
            disabled={isFormDisabled}
            errors={formState.errors}
            fieldName="initialBoosterCount"
            keyboardType="numeric"
            label="Initial Booster count"
            required={true}
          />
          <UniText>
            Optional: After completing any initial boosters, specify how often
            (in years) this vaccine needs to be readministered.
          </UniText>
          <FormFieldText
            control={control}
            disabled={isFormDisabled}
            errors={formState.errors}
            fieldName="longTermBoosterCadence"
            keyboardType="numeric"
            label="Yearly cadence"
            required={true}
          />
          <UniText>Optional: Age-based Boosters</UniText>
          <UniText>
            Some vaccines need to be given repeatedly until the animal reaches a
            certain age, regardless of how many shots they've received. For
            example, puppies need DHPP vaccines until they're 16 weeks old.
            Enter the target age (in weeks) if this applies.
          </UniText>
          <UniText>
            When left blank, the system will only use the booster count settings
            above if specified.
          </UniText>
          <FormFieldText
            control={control}
            disabled={isFormDisabled}
            errors={formState.errors}
            fieldName="initialBoosterRecurringAgeWeeks"
            keyboardType="numeric"
            label="Repeat Boosters until age"
            required={true}
          />
          <UniDivider />
        </View>
        <View style={styles.vaccineSection}>
          <UniText variant="headlineSmall">Alternative Vaccines</UniText>
          <UniText>
            Optional: Link equivalent vaccines that can be used as substitutes.
            For example, DHPP/Lepto can be used in place of DHLPP. This helps
            the scheduling system recognize valid alternative vaccinations.
          </UniText>
          <FormFieldMultiSelect
            control={control}
            data={vaccineList}
            disable={isFormDisabled}
            errors={formState.errors}
            fieldName="supersededByVaccineIds"
            label="Alternative Vaccines"
            required={false}
            search={true}
            searchField="label"
            searchPlaceholder="Search for a vaccine"
          />
        </View>
        <UniButton
          disabled={isFormDisabled}
          mode="contained"
          onPress={async () =>
            deleteVaccineMutation.mutate(
              {
                id: vaccineId,
              },
              {
                onSuccess: async () => {
                  await utils.vaccine.list.invalidate()
                  router.dismissTo(
                    buildOrgPath(`/org-settings/${currentOrganization?.id}/settings`)
                  )
                },
              }
            )
          }
          testID="delete-vaccine-button"
        >
          Delete
        </UniButton>
      </Card>
    </Page>
  )
}

export default OrganizationEditVaccine

const styles = StyleSheet.create((theme) => {
  return {
    flexContainer: {
      flex: 1,
    },
    vaccineRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.tokens.spacing[4],
    },
    vaccineSection: {
      gap: theme.tokens.spacing[4],
      marginBottom: theme.tokens.spacing[4],
    },
  }
})
